// src/feature/FeatureContext.js

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import apiClient from '../services/api';
import { useAuth } from '../auth/AuthContext';

const FeatureContext = createContext({
    features: {},
    isLoading: true,
    refetchFeatures: () => {},
});

export const FeatureProvider = ({ children }) => {
    const [features, setFeatures] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const { currentUser, isLoading: isAuthLoading } = useAuth();

    // Tworzymy stabilną wartość logiczną, aby uniknąć problemów z referencją obiektu.
    const isAuthenticated = !!currentUser;

    const fetchFeatures = useCallback(async () => {
        setIsLoading(true);
        try {
            let response;
            if (isAuthenticated) {
                // Użytkownik jest zalogowany, pobieramy jego spersonalizowaną konfigurację
                response = await apiClient.get('/features/config');
            } else {
                // Użytkownik jest gościem, pobieramy konfigurację publiczną
                response = await apiClient.get('/features/public-config');
            }
            // Zapewniamy, że features zawsze jest obiektem
            setFeatures(response.data || {});
        } catch (error) {
            console.error("Błąd podczas pobierania konfiguracji funkcji:", error);
            setFeatures({}); // W przypadku błędu ustawiamy pusty obiekt
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        // Efekt uruchomi się ponownie, gdy zakończy się ładowanie autentykacji,
        // lub gdy użytkownik się zaloguje/wyloguje (zmiana isAuthenticated).
        if (!isAuthLoading) {
            fetchFeatures();
        }
    }, [isAuthLoading, fetchFeatures]);

    const value = {
        features,
        isLoading,
        refetchFeatures: fetchFeatures
    };

    return (
        <FeatureContext.Provider value={value}>
            {children}
        </FeatureContext.Provider>
    );
};

export const useFeatures = () => useContext(FeatureContext);